import React, { useState, useEffect } from 'react';
import { Layout, Button } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { Header, Content } from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSecureService } from '../../store/service';
import SidebarContent from '../Sidebar/SidebarContent';
import styles from './AuthChecker.module.scss';

interface AuthCheckerProps {
  children: React.ReactNode;
  isMobile: boolean;
  selectedKeys: string[];
  onMenuClick: ({ key }: { key: string }) => void;
  onMobileDrawerOpen: () => void;
}

const AuthChecker: React.FC<AuthCheckerProps> = ({
  children,
  isMobile,
  selectedKeys,
  onMenuClick,
  onMobileDrawerOpen
}) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { getEntryByPath, startSessionCheck, refreshUserInfo } = useSecureService();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isFileDetailPage, setIsFileDetailPage] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (window.location.pathname === '/login') {
        setIsCheckingAuth(false);
        setAuthChecked(true);
        return;
      }

      // Auth kontrolü sadece bir kez yapılsın
      if (authChecked) {
        return;
      }

      setIsCheckingAuth(true);
      
      // Path kontrolü ile FileDetail sayfasını tespit et
      const currentPath = window.location.pathname.substring(1); // Başındaki /'yi kaldır
      const isVersionsPage = currentPath.endsWith('/versions');
      const cleanPath = isVersionsPage ? currentPath.replace('/versions', '') : currentPath;
      
      let isFilePage = false;
      
      if (cleanPath && cleanPath.includes('/')) {
        try {
          const entryResponse = await getEntryByPath(cleanPath);
          if (entryResponse.success && entryResponse.data) {
            isFilePage = entryResponse.data.entryType === 'File';
          }
        } catch (error) {
          // Path servisi hata verirse varsayılan olarak FileDetail sayfası değil kabul et
          isFilePage = false;
        }
      }
      
      setIsFileDetailPage(isFilePage);
      
      // FileDetail sayfası dışındaki tüm sayfalarda login kontrolü
      if (!isAuthenticated && !isFilePage) {
        window.location.href = '/login';
        return;
      }
      
      setIsCheckingAuth(false);
      setAuthChecked(true);
    };
  
    init();
  }, [isAuthenticated, authChecked, getEntryByPath]); 

  // Düzenli oturum kontrolü ve notification count güncelleme başlat
  useEffect(() => {
    if (isAuthenticated && !window.location.pathname.includes('/login')) {
      const cleanup = startSessionCheck();
      return cleanup;
    }
  }, [isAuthenticated, startSessionCheck]);

  // Sayfa değiştiğinde me servisini çağır (sadece bir kez)
  useEffect(() => {
    if (isAuthenticated && !window.location.pathname.includes('/login')) {
      const callMeService = async () => {
        try {
          await refreshUserInfo();
        } catch (error) {
          console.error('Error calling me service on page change:', error);
        }
      };
      
      callMeService();
    }
  }, [location.pathname]); // Sadece pathname değiştiğinde çağrılır

  if (isCheckingAuth) {
    return null;
  }

  return <>{children}</>;
};

export default AuthChecker; 